import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Plus, Trash2, Loader2, Building2, Package,
  ChevronDown, ChevronRight, Rows3, Layers, Lock, Unlock, ShieldAlert, ListChecks, LayoutGrid,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';

const SeasonalLocationSetup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ hall_name: '', floor_name: '', row_name: '', position_name: '', capacity_boxes: '' });
  const [halls, setHalls] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // tabs
  const [activeTab, setActiveTab] = useState('hierarchy'); // 'hierarchy' | 'summary'

  // expand state (keyed by footprint)
  const [expanded, setExpanded] = useState({});

  // locks
  const [locksByLoc, setLocksByLoc] = useState({}); // location_id -> lock
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockTarget, setLockTarget] = useState(null); // {location_id, footprint, level}
  const [lockPutAway, setLockPutAway] = useState(false);
  const [lockPicking, setLockPicking] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [savingLock, setSavingLock] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [locRes, hallRes, lockRes] = await Promise.all([
        api.getLocations(),           // ALL levels for the tree
        api.getHalls(),
        api.getBinLocks().catch(() => ({ locks: [] })),
      ]);
      setLocations(locRes.locations || []);
      setHalls(hallRes.halls || []);
      const m = {};
      (lockRes.locks || []).forEach((l) => { m[l.location_id] = l; });
      setLocksByLoc(m);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load locations');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fetchLocks = async () => {
    try {
      const res = await api.getBinLocks();
      const m = {};
      (res.locks || []).forEach((l) => { m[l.location_id] = l; });
      setLocksByLoc(m);
    } catch { setLocksByLoc({}); }
  };

  const fetchSummary = async () => {
    try {
      setSummaryLoading(true);
      const res = await api.getBinLocksSummary();
      setSummary(res.summary || []);
    } catch (e) {
      toast.error('Failed to load locked bins summary');
    } finally { setSummaryLoading(false); }
  };
  useEffect(() => { if (activeTab === 'summary') fetchSummary(); }, [activeTab]); // eslint-disable-line

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.hall_name.trim() || !form.floor_name.trim() || !form.row_name.trim() || !form.position_name.trim()) {
      toast.error('Hall, Floor, Row and Position are all required to create a bin'); return;
    }
    setSaving(true);
    try {
      const payload = {
        hall_name: form.hall_name.trim(), floor_name: form.floor_name.trim(),
        row_name: form.row_name.trim(), position_name: form.position_name.trim(),
        capacity_boxes: form.capacity_boxes ? parseInt(form.capacity_boxes, 10) : null,
      };
      const res = await api.createLocation(payload);
      toast.success(res.message || 'Bin created');
      setForm((p) => ({ ...p, position_name: '', capacity_boxes: '' }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create bin');
    } finally { setSaving(false); }
  };

  const del = async (loc) => {
    if (!window.confirm(`Delete '${loc.footprint}'?`)) return;
    try { await api.deleteLocation(loc.location_id); toast.success('Deleted'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  // ── Lock helpers ──────────────────────────────────────────────
  const openLockDialog = (loc) => {
    // Prefill from the most-specific lock affecting this node (self or ancestor)
    // so unticking releases exactly THIS node (creating a per-node override) —
    // the parent stays as-is.
    const best = mostSpecificLock(loc);
    setLockTarget({ location_id: loc.location_id, footprint: loc.footprint, level: loc.level });
    setLockPutAway(best?.block_put_away || false);
    setLockPicking(best?.block_picking || false);
    setLockReason((best && best.location_id === loc.location_id ? best.reason : '') || '');
    setLockDialogOpen(true);
  };

  const saveLock = async () => {
    if (!lockTarget) return;
    try {
      setSavingLock(true);
      await api.setBinLock({
        location_id: lockTarget.location_id,
        block_put_away: lockPutAway,
        block_picking: lockPicking,
        reason: lockReason,
      });
      const released = !(lockPutAway || lockPicking);
      toast.success(released ? `Released ${lockTarget.footprint}` : `Updated lock on ${lockTarget.footprint}`);
      setLockDialogOpen(false);
      await fetchLocks();
      if (activeTab === 'summary') fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update lock');
    } finally { setSavingLock(false); }
  };

  // The MOST-SPECIFIC lock affecting a node: the doc whose footprint is the
  // longest prefix (self or nearest ancestor). A child's own record wins.
  const mostSpecificLock = (loc) => {
    let best = null; let bestLen = -1;
    for (const l of Object.values(locksByLoc)) {
      const p = l?.footprint;
      if (!p || !loc?.footprint) continue;
      if (loc.footprint === p || loc.footprint.startsWith(p + ' > ')) {
        if (p.length > bestLen) { bestLen = p.length; best = l; }
      }
    }
    return best;
  };

  // Most-specific-node-wins: only the nearest lock decides. A child's own record
  // (including an explicit release override with both switches off) beats an
  // ancestor lock — so any node can be released independently.
  const getEffectiveLock = (loc) => {
    const best = mostSpecificLock(loc);
    if (!best || !(best.block_put_away || best.block_picking)) return null;
    return { lock: best, inherited: best.location_id !== loc.location_id };
  };

  const LockControl = ({ loc }) => {
    const eff = getEffectiveLock(loc);
    const lock = eff?.lock;
    const inherited = eff?.inherited;
    const isLocked = !!lock;
    let title = 'Lock this location for put-away / picking';
    if (isLocked) {
      const parts = [];
      if (lock.block_put_away) parts.push('Put-Away');
      if (lock.block_picking) parts.push('Picking');
      title = inherited
        ? `LOCKED via parent "${lock.node_label || lock.footprint}": ${parts.join(' + ')}${lock.reason ? ` — ${lock.reason}` : ''}. Release it on the parent node.`
        : `LOCKED: ${parts.join(' + ')}${lock.reason ? ` — ${lock.reason}` : ''}`;
    }
    const badgeCls = inherited ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
    return (
      <div className="flex items-center gap-1">
        {isLocked && (
          <span className={`flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${badgeCls}`} title={title} data-testid={`sls-lock-badge-${loc.location_id}`}>
            <ShieldAlert className="w-3 h-3" />
            {lock.block_put_away ? 'PA' : ''}{lock.block_put_away && lock.block_picking ? '+' : ''}{lock.block_picking ? 'PK' : ''}
            {inherited ? ' ↧' : ''}
          </span>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); openLockDialog(loc); }}
          className={isLocked ? (inherited ? 'text-amber-600 hover:text-amber-700 h-7 px-1.5' : 'text-red-600 hover:text-red-700 h-7 px-1.5') : 'text-gray-400 hover:text-gray-700 h-7 px-1.5'}
          title={title}
          data-testid={`sls-lock-btn-${loc.location_id}`}
        >
          {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </Button>
      </div>
    );
  };

  // ── Build Hall > Floor > Row > Position tree ─────────────────
  const toggle = (fp) => setExpanded((p) => ({ ...p, [fp]: !p[fp] }));
  const childrenOf = (level, parentFp) => {
    const parentDepth = parentFp ? parentFp.split(' > ').length : 0;
    return locations
      .filter((l) => l.level === level
        && (parentFp ? (l.footprint.startsWith(parentFp + ' > ') && l.footprint.split(' > ').length === parentDepth + 1) : true))
      .sort((a, b) => (a.footprint || '').localeCompare(b.footprint || ''));
  };

  const hallNodes = childrenOf('hall', null);

  const renderPosition = (pos) => (
    <div key={pos.location_id} className="ml-6 border-l-2 border-orange-200">
      <div className="flex items-center justify-between p-2 bg-cyan-50" data-testid={`sls-node-position-${pos.location_id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <Package className="w-4 h-4 text-sky-600 shrink-0" />
          <span className="font-mono text-sm font-medium text-[#1A1F2E]">{pos.position_name}</span>
          <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">Position</span>
          {pos.current_customer
            ? <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[11px]">{pos.current_customer}</Badge>
            : <Badge className="bg-gray-100 text-gray-500 border-0 text-[11px]">Empty</Badge>}
          <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px]">
            {pos.box_count || 0}{pos.capacity_boxes ? `/${pos.capacity_boxes}` : ''} boxes
          </Badge>
          {pos.status === 'full' && <Badge className="bg-red-100 text-red-700 border-0 text-[11px]">FULL</Badge>}
        </div>
        <div className="flex items-center gap-1">
          <LockControl loc={pos} />
          <Button variant="ghost" size="sm" onClick={() => del(pos)} className="text-red-500 hover:bg-red-50 h-7 px-1.5" data-testid={`sls-del-${pos.location_id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderRow = (row) => {
    const positions = childrenOf('position', row.footprint);
    const open = expanded[row.footprint];
    return (
      <div key={row.location_id} className="ml-6 border-l-2 border-green-200">
        <div className="flex items-center justify-between p-2 bg-purple-50 cursor-pointer" onClick={() => toggle(row.footprint)} data-testid={`sls-node-row-${row.location_id}`}>
          <div className="flex items-center gap-2">
            {positions.length > 0
              ? (open ? <ChevronDown className="w-4 h-4 text-purple-600" /> : <ChevronRight className="w-4 h-4 text-purple-600" />)
              : <div className="w-4" />}
            <Layers className="w-4 h-4 text-purple-600" />
            <span className="text-[#1A1F2E]">{row.row_name}</span>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Row</span>
            <span className="text-xs text-[#5A5F6E]">({positions.length} positions)</span>
          </div>
          <LockControl loc={row} />
        </div>
        {open && positions.map(renderPosition)}
      </div>
    );
  };

  const renderFloor = (floor) => {
    const rows = childrenOf('row', floor.footprint);
    const open = expanded[floor.footprint];
    return (
      <div key={floor.location_id} className="ml-6 border-l-2 border-blue-200">
        <div className="flex items-center justify-between p-2 bg-green-50 cursor-pointer" onClick={() => toggle(floor.footprint)} data-testid={`sls-node-floor-${floor.location_id}`}>
          <div className="flex items-center gap-2">
            {rows.length > 0
              ? (open ? <ChevronDown className="w-4 h-4 text-green-600" /> : <ChevronRight className="w-4 h-4 text-green-600" />)
              : <div className="w-4" />}
            <Rows3 className="w-4 h-4 text-green-600" />
            <span className="text-[#1A1F2E]">{floor.floor_name}</span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Floor</span>
            <span className="text-xs text-[#5A5F6E]">({rows.length} rows)</span>
          </div>
          <LockControl loc={floor} />
        </div>
        {open && rows.map(renderRow)}
      </div>
    );
  };

  const renderHall = (hall) => {
    const floors = childrenOf('floor', hall.footprint);
    const open = expanded[hall.footprint];
    return (
      <div key={hall.location_id} className="border border-[#E5DFD0] rounded-lg" data-testid={`sls-node-hall-${hall.location_id}`}>
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-t-lg cursor-pointer" onClick={() => toggle(hall.footprint)}>
          <div className="flex items-center gap-2 flex-wrap">
            {floors.length > 0
              ? (open ? <ChevronDown className="w-4 h-4 text-blue-600" /> : <ChevronRight className="w-4 h-4 text-blue-600" />)
              : <div className="w-4" />}
            <Building2 className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-[#1A1F2E]">{hall.hall_name}</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Hall</span>
            <span className="text-xs text-[#5A5F6E]">({floors.length} floors)</span>
          </div>
          <LockControl loc={hall} />
        </div>
        {open && floors.map(renderFloor)}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-location-setup-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/seasonal-bundle')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="sls-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <MapPin className="w-6 h-6 mr-3 text-sky-400" />
          <div>
            <h1 className="text-xl font-bold font-display">Location Setup</h1>
            <p className="text-sm text-[#E8DCC8]">Seasonal Bundle WMS — Halls › Floors › Rows › Positions</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8 grid lg:grid-cols-3 gap-6">
        {/* Add Bin */}
        <Card className="lg:col-span-1 h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add Bin</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs text-[#5A5F6E]">Hall</label>
              <Input list="sb-halls" value={form.hall_name} onChange={setF('hall_name')} placeholder="e.g. SH1" data-testid="sls-hall-input" />
              <datalist id="sb-halls">{halls.map((h) => <option key={h} value={h} />)}</datalist>
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">Floor</label>
              <Input value={form.floor_name} onChange={setF('floor_name')} placeholder="e.g. GF" data-testid="sls-floor-input" />
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">Row</label>
              <Input value={form.row_name} onChange={setF('row_name')} placeholder="e.g. R1" data-testid="sls-row-input" />
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">Position (Bin)</label>
              <Input value={form.position_name} onChange={setF('position_name')} placeholder="e.g. P1" data-testid="sls-position-input" />
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">Capacity (boxes, optional)</label>
              <Input type="number" min="0" value={form.capacity_boxes} onChange={setF('capacity_boxes')} placeholder="e.g. 10" data-testid="sls-capacity-input" />
            </div>
            <Button onClick={submit} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="sls-add-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Add Bin
            </Button>
            <p className="text-[11px] text-[#8A8F9E]">Missing Hall/Floor/Row are auto-created. Lock any node with the lock icon — it cascades to every position below it.</p>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="flex items-center gap-2" data-testid="sls-tabs">
            <button
              onClick={() => setActiveTab('hierarchy')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'hierarchy' ? 'bg-sky-600 text-white' : 'bg-white border border-[#E5DFD0] text-[#5A5F6E] hover:bg-gray-50'}`}
              data-testid="sls-tab-hierarchy"
            >
              <LayoutGrid className="w-4 h-4 inline mr-2" />Hierarchy
            </button>
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'summary' ? 'bg-sky-600 text-white' : 'bg-white border border-[#E5DFD0] text-[#5A5F6E] hover:bg-gray-50'}`}
              data-testid="sls-tab-summary"
            >
              <ListChecks className="w-4 h-4 inline mr-2" />Locked Bins Summary
            </button>
          </div>

          {activeTab === 'hierarchy' && (
            <div data-testid="sls-bin-list" className="space-y-2">
              {loading ? (
                <p className="text-center text-[#5A5F6E] py-10"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</p>
              ) : hallNodes.length === 0 ? (
                <div className="text-center text-[#5A5F6E] py-10 bg-white rounded-xl border border-[#E5DFD0]" data-testid="sls-empty">No bins yet. Add your first bin on the left.</div>
              ) : hallNodes.map(renderHall)}
            </div>
          )}

          {activeTab === 'summary' && (
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6" data-testid="sls-locked-summary">
              <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">Seasonal Locked Bins Summary</h2>
              {summaryLoading ? (
                <div className="text-center py-8 text-[#5A5F6E]">Loading...</div>
              ) : summary.length === 0 ? (
                <div className="text-center py-12">
                  <Unlock className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
                  <p className="text-[#5A5F6E]">No locked bins for seasonal storage.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[#5A5F6E] border-b border-[#E5DFD0]">
                        <th className="py-2 pr-4">Locked Node</th>
                        <th className="py-2 pr-4">Scope</th>
                        <th className="py-2 pr-4">Blocked</th>
                        <th className="py-2 pr-4">Positions</th>
                        <th className="py-2 pr-4">Stored Boxes</th>
                        <th className="py-2 pr-4">Locked Date</th>
                        <th className="py-2 pr-4">Locked By</th>
                        <th className="py-2 pr-4">Last Put-Away</th>
                        <th className="py-2 pr-4">Last Pick</th>
                        <th className="py-2 pr-4">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((r) => (
                        <tr key={r.lock_id} className="border-b border-[#F0EBE0]" data-testid={`sls-summary-row-${r.lock_id}`}>
                          <td className="py-2 pr-4 font-mono font-medium text-[#1A1F2E]">{r.node_label}</td>
                          <td className="py-2 pr-4 capitalize">{r.scope}</td>
                          <td className="py-2 pr-4">
                            <div className="flex gap-1">
                              {r.block_put_away && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Put-Away</span>}
                              {r.block_picking && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Picking</span>}
                            </div>
                          </td>
                          <td className="py-2 pr-4">{r.affected_bins}</td>
                          <td className="py-2 pr-4">{r.stored_bundles}</td>
                          <td className="py-2 pr-4 whitespace-nowrap">{r.locked_at ? new Date(r.locked_at).toLocaleString() : '—'}</td>
                          <td className="py-2 pr-4">{r.locked_by || '—'}</td>
                          <td className="py-2 pr-4 whitespace-nowrap">{r.last_put_away_date ? new Date(r.last_put_away_date).toLocaleString() : '—'}</td>
                          <td className="py-2 pr-4 whitespace-nowrap">{r.last_pick_date ? new Date(r.last_pick_date).toLocaleString() : '—'}</td>
                          <td className="py-2 pr-4 max-w-[180px] truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-[#5A5F6E] mt-3">To release or change a lock, use the lock icon in the Hierarchy tab.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Lock / Release Dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" />
              Lock / Release — {lockTarget?.footprint}
            </DialogTitle>
            <DialogDescription>
              Locking a <span className="font-medium capitalize">{lockTarget?.level}</span> applies to it and every position beneath it. Choose which transactions to block.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {(() => {
              const eff = lockTarget ? getEffectiveLock({ location_id: lockTarget.location_id, footprint: lockTarget.footprint }) : null;
              if (eff?.inherited) {
                return (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2.5" data-testid="sls-lock-inherited-notice">
                    Currently locked via parent <span className="font-semibold">{eff.lock.node_label || eff.lock.footprint}</span>. Your choice here applies to THIS {lockTarget?.level} and everything under it — untick both to release just this node (the parent stays locked).
                  </div>
                );
              }
              return null;
            })()}
            <label className="flex items-center gap-3 p-3 rounded-lg border border-[#E5DFD0] cursor-pointer hover:bg-gray-50" data-testid="sls-lock-toggle-putaway">
              <input type="checkbox" checked={lockPutAway} onChange={(e) => setLockPutAway(e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
              <div>
                <span className="text-sm font-medium text-[#1A1F2E]">Block Put-Away</span>
                <p className="text-xs text-[#5A5F6E]">No box put-away or relocation into these bins; they won't be suggested.</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-[#E5DFD0] cursor-pointer hover:bg-gray-50" data-testid="sls-lock-toggle-picking">
              <input type="checkbox" checked={lockPicking} onChange={(e) => setLockPicking(e.target.checked)} className="w-4 h-4 text-red-600 rounded" />
              <div>
                <span className="text-sm font-medium text-[#1A1F2E]">Block Picking</span>
                <p className="text-xs text-[#5A5F6E]">Boxes here won't be offered or allocated to any pick slip.</p>
              </div>
            </label>
            <div>
              <Label>Reason (optional)</Label>
              <Input value={lockReason} onChange={(e) => setLockReason(e.target.value)} placeholder="e.g. Stock audit, maintenance" className="mt-1" data-testid="sls-lock-reason-input" />
            </div>
            {!(lockPutAway || lockPicking) && (
              <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
                Both options off — saving will RELEASE this location.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={saveLock}
              disabled={savingLock}
              className={(lockPutAway || lockPicking) ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
              data-testid="sls-save-lock-btn"
            >
              {savingLock ? 'Saving...' : (lockPutAway || lockPicking) ? 'Apply Lock' : 'Release'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonalLocationSetup;
